// @ts-nocheck

// ─── Re-export shared constants ─────────────────────────────────────────────
export {
	STORES_TABLE_ID,
	SKUS_TABLE_ID,
	WAREHOUSES_TABLE_ID,
	ORDER_STATUS_PARAMS,
	EXCLUDED_FILTER_COLUMNS,
	COLUMN_EXCLUDE_KW,
	COLUMN_WAREHOUSE_KW,
	COLUMN_STORE_KW,
	COLUMN_ORDER_KW,
	PAGE_SIZE,
	MAX_BACKGROUND_PAGES,
	DEBOUNCE_SAVE_MS,
	VALIDATION_ERROR_DISPLAY_MS,
	AVAILABILITY_CAP_ERROR_MS,
	BULK_ITEMS_PER_PAGE,
	SELECT_ROW_KEY,
	GRID_HEADER_HEIGHT,
	GRID_ROW_HEIGHT,
	GRID_FONT_SIZE,
	GRID_MIN_COL_WIDTH,
} from 'pages/orderShared/constants';

// ─── Variant-specific constants ─────────────────────────────────────────────

/** AG Grid table ID for Order SKUs data (same underlying data as Order By SKU) */
export const ORDER_SKUS_TABLE_ID = 1;

export const PREFIX = 'orderByStore';
export const DEFAULT_EXPORT_FILENAME = 'OrderByStore_Export';

export const COLUMN_SKU_KW = [
	'sku',
	'genus',
	'brand',
	'size',
	'season',
	'tabletype',
	'table type',
	'subclass',
	'subcategory',
	'consignment',
	'isc',
	'parent sku',
	'erp sku',
	'sa4',
	'sa5',
	'sa6',
];

/** Fields to always exclude from auto-discovery (internal IDs, timestamps, system cols, store-level cols).
 *  For Order By Store, store fields repeat per SKU row — so we exclude store-level fields
 *  and keep SKU fields visible since those are the grid rows. */
export const EXCLUDED_GRID_FIELDS = new Set([
	'OrderDetailID',
	'BulkOrderDetailTmpID',
	'OrderID',
	'OrderKitID',
	'SKUID',
	'UOMID',
	'PlanogramAttributeID',
	'AdDetailID',
	'HealthCheckID',
	'InitialDataSource',
	'InitialUpdate',
	'DataSource',
	'LastUpdate',
	'LastUpdateUser',
	'OrderQty',
	'IsReadOnly',
	'_availError',
	/* Store-level fields — grid is at SKU-order level per store, these repeat for every row */
	'StoreDescription',
	'Store',
	'VolumeClass',
	'District',
	'Region',
	'City',
	'State',
	'Buyer',
	'Area',
	'StoreAttribute1',
	'StoreAttribute2',
	'StoreAttribute3',
	'StoreAttribute4',
	'StoreAttribute5',
	'StoreAttribute6',
	'StoreAttribute7',
	'StoreAttribute8',
	'StoreAttribute9',
	'StoreAttribute10',
	'CustomerNum',
	'ERPStoreID',
	'PostalCode',
	'Address',
	'Address2',
	'StoreID',
	'IsActive',
]);

/* ─── AG Grid Column Config (Store page — SKU-centric rows) ─── */
export const STORE_COL_CONFIG = [
	{ field: 'SKU', headerName: 'SKU', width: 90, pinned: 'left', enableRowGroup: true },
	{ field: 'SKUDescription', headerName: 'Description', width: 180 },
	{ field: 'Brand', headerName: 'Brand', width: 90, enableRowGroup: true },
	{ field: 'Size', headerName: 'Size', width: 70, enableRowGroup: true },
	{ field: 'Season', headerName: 'Season', width: 80, enableRowGroup: true, hide: true },
	{ field: 'Warehouse', headerName: 'WH', width: 80, enableRowGroup: true },
	{ field: 'QtyPerShelf', headerName: 'Shelf', width: 60, type: 'numericColumn' },
	{ field: 'OnHand', headerName: 'OH', width: 70, type: 'numericColumn' },
	{ field: 'ProjectedOnHand', headerName: 'Proj OH', width: 80, type: 'numericColumn' },
	{ field: 'OnHandInTransit', headerName: 'In Trans', width: 80, type: 'numericColumn' },
	{ field: 'SuggestedQty', headerName: 'Sugg', width: 70, type: 'numericColumn' },
	{
		field: 'CurrentWOS',
		headerName: 'WOS',
		width: 70,
		type: 'numericColumn',
		valueFormatter: (p: any) => (p.value != null ? Number(p.value).toFixed(1) : ''),
	},
	{
		field: 'ProjectedWOS',
		headerName: 'P.WOS',
		width: 70,
		type: 'numericColumn',
		valueFormatter: (p: any) => (p.value != null ? Number(p.value).toFixed(1) : ''),
	},
	{
		field: 'CurrentProjectedWOS',
		headerName: 'CP WOS',
		width: 80,
		type: 'numericColumn',
		hide: true,
		valueFormatter: (p: any) => (p.value != null ? Number(p.value).toFixed(1) : ''),
	},
	{ field: 'Leading7DayAIForecastQty', headerName: '7d Fcst', width: 80, type: 'numericColumn' },
	{ field: 'Leading14DayAIForecastQty', headerName: '14d Fcst', width: 80, type: 'numericColumn' },
	{ field: 'Leading21DayAIForecastQty', headerName: '21d Fcst', width: 80, type: 'numericColumn', hide: true },
	{ field: 'Leading14DayNaiveForecastQty', headerName: '14d Naive', width: 85, type: 'numericColumn', hide: true },
	{ field: '30DayNaiveForecastQty', headerName: '30d Naive', width: 85, type: 'numericColumn', hide: true },
	{ field: 'Trailing14DaySalesQty', headerName: 'T14 Sls', width: 80, type: 'numericColumn' },
	{ field: 'Trailing28DaySalesQty', headerName: 'T28 Sls', width: 80, type: 'numericColumn' },
	{
		field: 'Trailing14DaySalesRetail',
		headerName: 'T14 $',
		width: 80,
		type: 'numericColumn',
		hide: true,
		valueFormatter: (p: any) => (p.value != null ? '$' + Number(p.value).toFixed(2) : ''),
	},
	{
		field: 'Trailing28DaySalesRetail',
		headerName: 'T28 $',
		width: 80,
		type: 'numericColumn',
		hide: true,
		valueFormatter: (p: any) => (p.value != null ? '$' + Number(p.value).toFixed(2) : ''),
	},
	{ field: 'DaysSinceLastSales', headerName: 'DsLS', width: 70, type: 'numericColumn', hide: true },
	{ field: 'CurrentAvailableQty', headerName: 'Avail', width: 70, type: 'numericColumn' },
	/* OrderQty is appended dynamically by BaseOrderGrid */
];

/** Quick-group button definitions — SKU-centric groupings */
export const STORE_GROUP_OPTIONS = [
	{ field: 'Brand', label: 'Brand' },
	{ field: 'Size', label: 'Size' },
	{ field: 'Warehouse', label: 'Warehouse' },
	{ field: 'Season', label: 'Season' },
];
